/**
 * AdminSupportInbox — read-only review surface for every Tommy concierge
 * thread on the platform. Lives at /support for users whose Supabase
 * `app_metadata.roles` contains `admin`.
 *
 * What it shows:
 *   - Left column (or full width on mobile): list of all threads, sorted
 *     by most recent activity. Search by user email/name, filter to
 *     handed-off-only.
 *   - Right column: full transcript of the selected thread, including
 *     timestamps + a "handoff" badge on the assistant turn that
 *     triggered an SMS escalation.
 *
 * Security posture:
 *   The bearer token attached to every request is verified server-side
 *   via requireAdmin() — a non-admin caller sees 401/403, never any
 *   thread data. This component does no client-side enforcement beyond
 *   "show the UI"; it never trusts itself for access decisions.
 *
 * UX notes:
 *   - The "Chat with Defera Support" CTA flips Support.jsx back into the regular
 *     customer chat view, so admins can dog-food the experience.
 *   - Mobile-friendly: list and detail stack with a back affordance.
 *   - Transcripts include **manager consults** (migration 094): when Tommy
 *     didn't know an answer he stopped and asked Stephanie before replying.
 *     Each consult renders collapsed, inline, immediately above the reply
 *     it produced. That content is internal-only — Stephanie's candid read
 *     on Tommy's blind spots — and reaches this component solely through
 *     the requireAdmin()-gated thread route. It must never be rendered on
 *     a customer-facing surface.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { MessageSquare, Search, ChevronLeft, RefreshCw, MessageCircle, ArrowRightLeft, Filter, Plane, MessagesSquare, ChevronDown, AlertTriangle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { getSpecialist } from '@/constants/conciergeSpecialists';
import VipBadge from '@/components/admin/VipBadge';

/**
 * Mobile note: Tommy users don't have profile photos, so we render the
 * inbox as a list of meta-rich rows (name, email, preview, badges)
 * instead of avatars. We also drop the page into pure single-column
 * mode below the `lg` breakpoint so it works as a one-handed admin
 * surface on a phone.
 */

const DEFAULT_PAGE_SIZE = 50;

/**
 * Human-friendly "x ago" formatter used on every list row. Avoids
 * pulling in a date library for what is otherwise a small surface area.
 */
function formatRelativeTime(iso) {
  if (!iso) return '';
  try {
    const then = new Date(iso).getTime();
    if (!Number.isFinite(then)) return '';
    const diff = Date.now() - then;
    if (diff < 60_000) return 'just now';
    const mins = Math.floor(diff / 60_000);
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

/**
 * Absolute timestamp shown inside the transcript view ("Jun 5, 11:02 AM").
 * Kept tight so it doesn't crowd message bubbles.
 */
function formatAbsoluteTimestamp(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

/**
 * Minimal Markdown-ish renderer matching the customer chat: bold runs,
 * inline code, and paragraph breaks. We keep it small here because the
 * inbox is read-only and links / lists / code blocks aren't expected.
 */
export function PreviewMarkdown({ text }) {
  if (!text) return null;
  const blocks = String(text).split(/\n{2,}/);
  return (
    <div className="space-y-2 leading-relaxed">
      {blocks.map((block, i) => (
        <p key={i} className="whitespace-pre-wrap">
          {block.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((part, j) => {
            if (/^\*\*[^*]+\*\*$/.test(part)) {
              return <strong key={j} className="font-semibold text-white">{part.slice(2, -2)}</strong>;
            }
            if (/^`[^`]+`$/.test(part)) {
              return (
                <code key={j} className="px-1 py-0.5 rounded bg-white/10 text-[12.5px] text-white/90">
                  {part.slice(1, -1)}
                </code>
              );
            }
            return <React.Fragment key={j}>{part}</React.Fragment>;
          })}
        </p>
      ))}
    </div>
  );
}

/**
 * Visual language for a consult's outcome.
 *
 * `handoff` is deliberately NOT styled as a failure. Stephanie concluding
 * "a human needs to take this" is the system working — Tommy asked instead
 * of guessing, and she routed it honestly. The genuine problem states are
 * `inconclusive` / `failed` (he went to her and came back with nothing, so
 * he answered on his own) and the low-confidence badge below.
 */
const CONSULT_OUTCOME_STYLES = {
  answered: { label: 'Answered', className: 'text-emerald-300/90 bg-emerald-500/12 border-emerald-400/25' },
  handoff: { label: 'Routed to human', className: 'text-brand-200 bg-brand-500/15 border-brand-400/25' },
  inconclusive: { label: 'Inconclusive', className: 'text-amber-300/90 bg-amber-500/12 border-amber-400/25' },
  failed: { label: 'Consult failed', className: 'text-red-300/90 bg-red-500/12 border-red-400/25' },
};

/**
 * One Tommy↔Stephanie consult, rendered inline in the transcript at the
 * turn it produced.
 *
 * Why this is here and not behind a link: the consult IS the explanation
 * for the reply above it. An auditor reading "Tommy told the customer X"
 * needs to see, in the same eyeline, that Stephanie told him X and how
 * sure she was. Making that a separate page means nobody looks.
 *
 * Collapsed by default — most consults are fine, and an expanded wall of
 * internal dialogue between every turn would bury the customer
 * conversation, which is still the primary thing being audited. The
 * summary line carries enough signal (outcome, confidence, why he asked)
 * to decide whether to open it.
 */
export function ConsultTranscript({ consult, defaultOpen = false }) {
  // defaultOpen: the InquiriesDesk pre-expands the consult that filed the
  // inquiry being worked — everywhere else consults start collapsed.
  const [open, setOpen] = useState(defaultOpen);
  const outcome = CONSULT_OUTCOME_STYLES[consult.outcome] || CONSULT_OUTCOME_STYLES.answered;

  // Stephanie's own confidence in what she told Tommy. Surfaced loudly
  // when low: an 'answered' consult where she was guessing is the single
  // highest-risk artifact this system can produce — Tommy sounded certain
  // to the customer because his manager signed off, and his manager was
  // not certain. That's exactly what an audit should catch.
  const lowConfidence = consult.confidence != null && consult.confidence <= 2;

  return (
    <div className="my-2.5 rounded-2xl border border-amber-400/15 bg-amber-500/[0.04] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3.5 py-2.5 text-left hover:bg-white/[0.03] transition-colors"
      >
        <MessagesSquare className="w-3.5 h-3.5 text-amber-300/80 flex-shrink-0" strokeWidth={1.75} />
        <span className="text-[10.5px] uppercase tracking-wider font-semibold text-amber-200/90 flex-shrink-0">
          Manager consult
        </span>
        <span className={`text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded border flex-shrink-0 ${outcome.className}`}>
          {outcome.label}
        </span>
        {lowConfidence ? (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-medium text-red-200 bg-red-500/15 border border-red-400/25 px-1.5 py-0.5 rounded flex-shrink-0">
            <AlertTriangle className="w-2.5 h-2.5" strokeWidth={2} />
            Low confidence {consult.confidence}/5
          </span>
        ) : null}
        <span className="text-[11px] text-white/40 truncate hidden sm:inline">
          {consult.turn_count} turns
          {consult.duration_ms != null ? ` · ${(consult.duration_ms / 1000).toFixed(1)}s` : ''}
        </span>
        <ChevronDown
          className={`w-3.5 h-3.5 text-white/40 ml-auto flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          strokeWidth={2}
        />
      </button>

      {open ? (
        <div className="px-3.5 pb-3.5 pt-1 space-y-2.5 border-t border-white/[0.05]">
          {/* Tommy's stated reason for escalating. Aggregated across
              consults this is a live map of the knowledge base's holes —
              it's the field worth reading even when the outcome is fine. */}
          <div className="rounded-xl bg-black/25 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider font-medium text-white/40 mb-1">
              Why Tommy escalated
            </div>
            <div className="text-[12.5px] text-white/80 leading-relaxed">{consult.trigger_reason}</div>
          </div>

          {consult.transcript?.length ? (
            <div className="space-y-2">
              {consult.transcript.map((t) => {
                const isTommy = t.role === 'tommy';
                return (
                  <div key={t.seq} className={`flex ${isTommy ? 'justify-start' : 'justify-end'}`}>
                    <div
                      className={`max-w-[88%] px-3 py-2 rounded-[16px] text-[13px] leading-relaxed ${
                        isTommy
                          ? 'bg-white/[0.05] text-white/85 rounded-bl-[5px] border border-white/[0.05]'
                          : 'bg-amber-500/[0.09] text-amber-50/90 rounded-br-[5px] border border-amber-400/20'
                      }`}
                    >
                      <div className={`text-[10px] uppercase tracking-wider font-medium mb-1 ${isTommy ? 'text-white/45' : 'text-amber-200/80'}`}>
                        {isTommy ? 'Tommy' : 'Stephanie'}
                      </div>
                      <PreviewMarkdown text={t.content} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-[12px] text-white/40 px-1">No transcript recorded for this consult.</div>
          )}

          <div className="flex items-center gap-3 flex-wrap pt-1 text-[10.5px] text-white/35">
            {consult.confidence != null ? <span>Stephanie&apos;s confidence: {consult.confidence}/5</span> : null}
            {consult.stephanie_model ? <span>{consult.stephanie_model}</span> : null}
            {consult.duration_ms != null ? <span>{(consult.duration_ms / 1000).toFixed(1)}s on hold</span> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** lg and up, where the conversation pane scrolls on its own (below it, the page does). */
const isWideScreen = () => typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(min-width: 1024px)').matches;

export default function AdminSupportInbox({ onSwitchToChat }) {
  const [threads, setThreads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [handedOffOnly, setHandedOffOnly] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState(null);

  /**
   * Latest in-flight list fetch. We use this to short-circuit stale
   * responses (typing fast in the search box would otherwise race).
   */
  const listFetchToken = useRef(0);

  const buildAuthHeader = useCallback(async () => {
    try {
      const { data } = await supabase.auth.getSession();
      const tok = data?.session?.access_token;
      return tok ? `Bearer ${tok}` : null;
    } catch {
      return null;
    }
  }, []);

  /**
   * Fetch the list of threads. Re-runs whenever search/filter changes,
   * with a 250ms debounce on the search field handled by an effect
   * below.
   */
  const loadThreads = useCallback(
    async (overrides = {}) => {
      const token = listFetchToken.current + 1;
      listFetchToken.current = token;
      setLoading(true);
      setError(null);
      try {
        const authHeader = await buildAuthHeader();
        if (!authHeader) {
          setError('Please sign back in — your session expired.');
          setThreads([]);
          setTotal(0);
          setLoading(false);
          return;
        }
        const params = new URLSearchParams();
        const searchTerm = overrides.search ?? search;
        const handedOff = overrides.handedOff ?? handedOffOnly;
        if (searchTerm) params.set('search', searchTerm);
        if (handedOff) params.set('handedOff', '1');
        params.set('limit', String(DEFAULT_PAGE_SIZE));

        const res = await fetch(`/api/admin/support-threads?${params.toString()}`, {
          headers: { Authorization: authHeader },
        });
        if (token !== listFetchToken.current) return; // a newer fetch took over
        if (res.status === 401 || res.status === 403) {
          setError("You don't have access to the concierge inbox.");
          setThreads([]);
          setTotal(0);
          setLoading(false);
          return;
        }
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data?.ok) {
          throw new Error(data?.error || `Inbox request failed (${res.status}).`);
        }
        setThreads(Array.isArray(data.threads) ? data.threads : []);
        setTotal(Number(data.total) || 0);
        setLoading(false);
      } catch (err) {
        if (token !== listFetchToken.current) return;
        setError(err?.message || 'Failed to load concierge inbox.');
        setLoading(false);
      }
    },
    [buildAuthHeader, search, handedOffOnly],
  );

  /**
   * Initial load + reload whenever the "handed off" filter flips.
   * Search has its own debounced effect below.
   */
  useEffect(() => {
    loadThreads({ handedOff: handedOffOnly });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handedOffOnly]);

  /**
   * Debounced search reload. 250ms keeps the inbox responsive without
   * thrashing the server while the admin is still typing.
   */
  useEffect(() => {
    const handle = setTimeout(() => {
      loadThreads({ search });
    }, 250);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  /**
   * Open a thread → fetch its full transcript.
   */
  const openThread = useCallback(
    async (threadId) => {
      setSelectedId(threadId);
      setSelectedThread(null);
      setThreadLoading(true);
      setThreadError(null);
      try {
        const authHeader = await buildAuthHeader();
        if (!authHeader) {
          setThreadError('Session expired. Please sign back in.');
          setThreadLoading(false);
          return;
        }
        const res = await fetch(`/api/admin/support-threads/${encodeURIComponent(threadId)}`, {
          headers: { Authorization: authHeader },
        });
        if (res.status === 401 || res.status === 403) {
          setThreadError("You don't have access to view this thread.");
          setThreadLoading(false);
          return;
        }
        if (res.status === 404) {
          setThreadError('That thread no longer exists.');
          setThreadLoading(false);
          return;
        }
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data?.ok) {
          throw new Error(data?.error || `Failed to load thread (${res.status}).`);
        }
        setSelectedThread({
          thread: data.thread,
          messages: data.messages || [],
          consults: data.consults || [],
        });
        setThreadLoading(false);
      } catch (err) {
        setThreadError(err?.message || 'Failed to load thread.');
        setThreadLoading(false);
      }
    },
    [buildAuthHeader],
  );

  const closeThread = useCallback(() => {
    setSelectedId(null);
    setSelectedThread(null);
    setThreadError(null);
  }, []);

  /**
   * A thread opens at its newest message (founder, 2026-10-22). On a wide
   * screen the transcript pane scrolls; on a phone the page does. Instant,
   * not smooth: a smooth scroll gets cut short by the renders that follow.
   */
  const transcriptRef = useRef(null);
  useEffect(() => {
    if (!selectedThread?.messages?.length) return undefined;
    const frame = requestAnimationFrame(() => {
      const pane = transcriptRef.current;
      if (pane && isWideScreen()) {
        pane.scrollTop = pane.scrollHeight;
      } else {
        window.scrollTo({ top: document.documentElement.scrollHeight });
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [selectedThread]);

  const stats = useMemo(() => {
    const handedOff = threads.filter((t) => t.handed_off).length;
    return {
      shown: threads.length,
      total,
      handedOff,
    };
  }, [threads, total]);

  // When an admin opens a thread on mobile, focus the screen on the
  // transcript by hiding the page header (title row + search). This
  // gives the conversation the full viewport on small phones; the
  // sticky "Back" affordance below puts the customer one tap away.
  const isThreadOpenMobile = !!selectedId;

  return (
    <>
      <Helmet>
        <title>Concierge Inbox — Defera Admin</title>
        {/* Intentionally NOT overriding the viewport. The app's global
            meta omits viewport-fit=cover so the Layout's fixed nav
            sits below the iOS status bar on every page; overriding it
            here pushes the nav under the notch and looks broken. iOS
            still respects the home-bar / status-bar safe areas
            automatically without viewport-fit=cover. */}
      </Helmet>

      <div className="min-h-[100dvh] bg-black text-white">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 pt-4 sm:pt-6 pb-8 sm:pb-12">
          {/* Header — hidden on mobile when a thread is open so the
              transcript gets the full viewport. */}
          <header
            className={`${isThreadOpenMobile ? 'hidden lg:flex' : 'flex'} items-center justify-between gap-3 mb-4 sm:mb-5`}
          >
            <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
              <div className="hidden sm:flex w-10 h-10 rounded-xl bg-white/[0.06] border border-white/[0.08] items-center justify-center flex-shrink-0">
                <MessageSquare className="w-5 h-5 text-brand-300" strokeWidth={1.75} />
              </div>
              <div className="min-w-0">
                <h1 className="text-[18px] sm:text-[22px] font-semibold tracking-tight truncate leading-tight">
                  Concierge Inbox
                </h1>
                <p className="text-[11.5px] sm:text-[12.5px] text-white/55 leading-snug">
                  {stats.total.toLocaleString()} thread{stats.total === 1 ? '' : 's'}
                  {stats.handedOff > 0 ? ` · ${stats.handedOff} handed off` : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => loadThreads()}
                className="inline-flex items-center justify-center text-[12.5px] font-medium text-white/65 hover:text-white w-9 h-9 sm:w-auto sm:h-auto sm:gap-1.5 sm:px-3 sm:py-1.5 rounded-full bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] transition"
                aria-label="Refresh inbox"
              >
                <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.75} />
                <span className="hidden sm:inline">Refresh</span>
              </button>
              <button
                type="button"
                onClick={onSwitchToChat}
                className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-white px-3 py-2 sm:py-1.5 rounded-full bg-brand-600 active:bg-brand-700 hover:bg-brand-500 transition shadow-sm whitespace-nowrap"
              >
                <MessageCircle className="w-3.5 h-3.5" strokeWidth={2} />
                <span className="hidden xs:inline sm:inline">Chat with Defera Support</span>
                <span className="xs:hidden sm:hidden">Chat</span>
              </button>
            </div>
          </header>

          {/* Search + filter row — same visibility rule as the header. */}
          <div className={`${isThreadOpenMobile ? 'hidden lg:flex' : 'flex'} items-center gap-2 mb-3 sm:mb-4`}>
            <div className="flex-1 relative min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35 pointer-events-none" strokeWidth={1.75} />
              <input
                type="search"
                inputMode="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name or email…"
                className="w-full pl-9 pr-3 py-2.5 sm:py-2 bg-white/[0.04] border border-white/[0.08] rounded-xl text-[16px] sm:text-[14px] text-white placeholder:text-white/35 focus:outline-none focus:border-brand-400/40 focus:bg-white/[0.06] transition"
                style={{ fontSize: 'max(16px, 0.875rem)' }} /* keep iOS from auto-zooming the page on focus */
              />
            </div>
            <button
              type="button"
              onClick={() => setHandedOffOnly((v) => !v)}
              className={`inline-flex items-center justify-center sm:justify-start gap-1.5 text-[12.5px] font-medium px-3 h-[42px] sm:h-auto sm:py-2 rounded-xl border transition flex-shrink-0 ${
                handedOffOnly
                  ? 'bg-brand-500/15 text-brand-200 border-brand-400/30'
                  : 'bg-white/[0.04] text-white/65 border-white/[0.08] hover:text-white'
              }`}
              aria-pressed={handedOffOnly}
              aria-label="Handed off only"
            >
              <Filter className="w-3.5 h-3.5" strokeWidth={1.75} />
              <span className="hidden sm:inline">Handed off only</span>
              <span className="sm:hidden">Handoffs</span>
            </button>
          </div>

          {/* Two-pane layout: list (left), detail (right). On mobile the
              right pane replaces the list entirely when a thread is open. */}
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,380px)_minmax(0,1fr)] gap-3 sm:gap-4">
            {/* === LIST === */}
            <div className={`${selectedId ? 'hidden lg:block' : 'block'}`}>
              <div className="bg-white/[0.025] border border-white/[0.06] rounded-2xl overflow-hidden">
                {loading && threads.length === 0 ? (
                  <div className="p-8 text-center text-[13px] text-white/55">Loading concierge threads…</div>
                ) : error ? (
                  <div className="p-6 text-[13px] text-rose-300">
                    {error}
                  </div>
                ) : threads.length === 0 ? (
                  <div className="p-8 text-center text-[13px] text-white/55">
                    {search || handedOffOnly
                      ? 'No threads match those filters yet.'
                      : 'No concierge threads on file yet. As customers chat with Tommy, they\'ll appear here.'}
                  </div>
                ) : (
                  <ul className="divide-y divide-white/[0.05]">
                    {threads.map((t) => {
                      const isActive = t.id === selectedId;
                      return (
                        <li key={t.id}>
                          <button
                            type="button"
                            onClick={() => openThread(t.id)}
                            className={`w-full text-left px-3.5 sm:px-4 py-3.5 sm:py-3 min-h-[64px] transition ${
                              isActive ? 'bg-brand-500/[0.08]' : 'active:bg-white/[0.06] hover:bg-white/[0.035]'
                            }`}
                          >
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-[14px] sm:text-[13.5px] font-medium text-white truncate">
                                  {t.user_first_name || (t.user_email ? t.user_email.split('@')[0] : 'Customer')}
                                </span>
                                <VipBadge userId={t.user_id || null} email={t.user_email || null} />
                                {t.handed_off ? (
                                  <span className="text-[10px] uppercase tracking-wider font-medium text-brand-200 bg-brand-500/15 px-1.5 py-0.5 rounded flex-shrink-0">
                                    Handoff
                                  </span>
                                ) : null}
                                <span className="ml-auto text-[11px] text-white/40 flex-shrink-0">
                                  {formatRelativeTime(t.last_message_at)}
                                </span>
                              </div>
                              {t.user_email ? (
                                <div className="text-[11.5px] text-white/55 truncate mt-0.5">
                                  {t.user_email}
                                </div>
                              ) : null}
                              <div className="text-[12.5px] text-white/75 mt-1 line-clamp-2 leading-snug">
                                {t.last_message_preview || <span className="italic text-white/35">No reply yet</span>}
                              </div>
                              <div className="flex items-center gap-3 mt-1.5 text-[10.5px] text-white/40">
                                <span>
                                  {t.message_count} message{t.message_count === 1 ? '' : 's'}
                                </span>
                                {t.booking_reference ? (
                                  <span className="inline-flex items-center gap-1 text-emerald-300/75">
                                    <Plane className="w-3 h-3" strokeWidth={1.75} />
                                    {t.booking_reference}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>

            {/* === DETAIL === */}
            <div className={`${selectedId ? 'block' : 'hidden lg:block'}`}>
              {!selectedId ? (
                <div className="hidden lg:flex h-full min-h-[60vh] items-center justify-center text-center text-[13px] text-white/40 bg-white/[0.02] border border-white/[0.05] rounded-2xl">
                  <div className="max-w-xs">
                    <MessageSquare className="w-8 h-8 mx-auto text-white/20 mb-3" strokeWidth={1.5} />
                    Pick a thread on the left to review the full conversation.
                  </div>
                </div>
              ) : (
                <div className="bg-white/[0.025] border border-white/[0.06] rounded-2xl overflow-hidden">
                  {/* Sticky thread header — keeps the customer's name and
                      the Back button visible while scrolling a long
                      transcript on mobile. */}
                  <div
                    className="flex items-center gap-2 px-3 sm:px-4 py-3 border-b border-white/[0.06] bg-black/85 backdrop-blur sticky top-0 z-10"
                    style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
                  >
                    <button
                      type="button"
                      onClick={closeThread}
                      className="lg:hidden inline-flex items-center justify-center -ml-1 w-9 h-9 rounded-full text-white/70 active:bg-white/10 hover:bg-white/[0.06] transition flex-shrink-0"
                      aria-label="Back to inbox"
                    >
                      <ChevronLeft className="w-5 h-5" strokeWidth={1.75} />
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <h2 className="text-[15px] sm:text-[14.5px] font-semibold truncate">
                          {selectedThread?.thread?.user_first_name
                            || (selectedThread?.thread?.user_email ? selectedThread.thread.user_email.split('@')[0] : 'Customer')}
                        </h2>
                        {selectedThread?.thread?.handed_off ? (
                          <span className="text-[10px] uppercase tracking-wider font-medium text-brand-200 bg-brand-500/15 px-1.5 py-0.5 rounded flex-shrink-0">
                            Handoff
                          </span>
                        ) : null}
                      </div>
                      <div className="text-[11.5px] text-white/55 truncate">
                        {selectedThread?.thread?.user_email || ''}
                        {selectedThread?.thread?.booking_reference ? (
                          <>
                            <span className="mx-1.5 text-white/25">·</span>
                            <span className="inline-flex items-center gap-1 text-emerald-300/85">
                              <Plane className="w-3 h-3" strokeWidth={1.75} />
                              {selectedThread.thread.booking_reference}
                            </span>
                          </>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  {threadLoading ? (
                    <div className="p-10 text-center text-[13px] text-white/55">Loading conversation…</div>
                  ) : threadError ? (
                    <div className="p-6 text-[13px] text-rose-300">{threadError}</div>
                  ) : !selectedThread ? (
                    <div className="p-10 text-center text-[13px] text-white/55">No conversation loaded.</div>
                  ) : selectedThread.messages.length === 0 ? (
                    <div className="p-10 text-center text-[13px] text-white/55">No messages in this thread.</div>
                  ) : (
                    /* On mobile we let the whole page scroll (no max-h)
                       so the URL bar can hide naturally. On desktop we
                       cap the height with dynamic viewport units so the
                       transcript pane lives inside the two-column layout. */
                    <div ref={transcriptRef} className="p-3 sm:p-5 lg:max-h-[calc(100dvh-220px)] lg:overflow-y-auto space-y-3">
                      {selectedThread.messages.map((m) => {
                        const isUser = m.role === 'user';
                        // A message from the team, posted in the customer's chat.
                        const isTeam = m.role === 'team';
                        // Consults render immediately BEFORE the reply they
                        // produced — which is also where they happened in
                        // real time: Tommy stalled, went to Stephanie, came
                        // back and said this. Reading top to bottom then
                        // matches the actual sequence of events.
                        const consultsHere = (selectedThread.consults || []).filter(
                          (c) => c.assistant_message_id === m.id,
                        );
                        return (
                          <React.Fragment key={m.id}>
                            {consultsHere.map((c) => (
                              <ConsultTranscript key={c.id} consult={c} />
                            ))}
                          <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                            <div
                              className={`max-w-[88%] sm:max-w-[82%] lg:max-w-[78%] px-3.5 sm:px-4 py-2.5 sm:py-3 rounded-[20px] text-[14px] ${
                                isUser
                                  ? 'bg-brand-600/85 text-white rounded-br-[6px]'
                                  : isTeam
                                    ? 'bg-emerald-500/[0.08] text-white/95 rounded-bl-[6px] border border-emerald-400/25'
                                    : 'bg-white/[0.07] text-white/95 rounded-bl-[6px] border border-white/[0.05]'
                              }`}
                            >
                              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                                <span className={`text-[10.5px] uppercase tracking-wider font-semibold ${isUser ? 'text-white/75' : isTeam ? 'text-emerald-200/85' : 'text-brand-300/85'}`}>
                                  {isUser ? 'Customer' : isTeam ? (m.author_label || 'Defera team') : getSpecialist(m.specialist_id).name}
                                </span>
                                {m.was_handoff_turn ? (
                                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-medium text-brand-200 bg-brand-500/15 px-1.5 py-0.5 rounded">
                                    <ArrowRightLeft className="w-2.5 h-2.5" strokeWidth={2} />
                                    Handoff
                                  </span>
                                ) : null}
                                {m.booking_reference ? (
                                  <span className="text-[10px] text-emerald-300/85">{m.booking_reference}</span>
                                ) : null}
                                <span className={`ml-auto text-[10.5px] ${isUser ? 'text-white/55' : 'text-white/40'}`}>
                                  {formatAbsoluteTimestamp(m.created_at)}
                                </span>
                              </div>
                              <PreviewMarkdown text={m.content} />
                            </div>
                          </div>
                          </React.Fragment>
                        );
                      })}

                      {/* Consults that never got pinned to a turn — the
                          customer disconnected mid-reply, or persisting the
                          turn failed. Rare, but they're still the record of
                          a real conversation Stephanie had, so they must not
                          silently vanish from an audit surface. */}
                      {(selectedThread.consults || [])
                        .filter((c) => !c.assistant_message_id
                          || !selectedThread.messages.some((m) => m.id === c.assistant_message_id))
                        .map((c) => (
                          <div key={c.id}>
                            <div className="text-[10.5px] uppercase tracking-wider font-semibold text-white/35 mb-1 px-1">
                              Unlinked consult — the reply it produced was never recorded
                            </div>
                            <ConsultTranscript consult={c} />
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
